import type { SignatureFields } from "./types.js";

/**
 * Kant-en-klare zakelijke handtekeningen.
 *
 * Waarom sjablonen en niet "typ zelf maar HTML": een handtekening is het enige
 * stukje vormgeving dat een gebruiker in zijn mail heeft, en vrijwel niemand
 * schrijft daar met plezier HTML voor. Hij vult zijn gegevens één keer in en
 * kiest een vorm; de HTML is het resultaat, niet de invoer.
 *
 * Twee regels waar elk sjabloon hieronder zich aan houdt:
 *
 *  1. ALLES INLINE. Mailclients gooien <style>-blokken weg (Gmail doet dat al
 *     twintig jaar), en onze eigen sanitizer laat ze ook niet door. Layout gaat
 *     daarom via <table> met inline stijl — lelijk naar webmaatstaven, maar het
 *     is wat Outlook 2016 nog tekent.
 *  2. ALLEEN WAT DE SANITIZER TOELAAT. De server saneert de handtekening met
 *     dezelfde allowlist als inkomende mail. Gebruikte eigenschappen staan daar
 *     allemaal in; `background` (shorthand), `position` en `float` bewust niet.
 *     Wie hier iets toevoegt: controleer het tegen sanitize.service.ts, anders
 *     verdwijnt het stilletjes bij het opslaan.
 *
 * De velden worden geëscaped voordat ze in de HTML belanden. Dat is de eerste
 * van twee lagen — de server saneert het resultaat daarna nog een keer.
 */

/** Pad waarop de webmail het eigen logo serveert; de server maakt hier bij het versturen een cid: van. */
export const LOGO_SRC = "/api/settings/signature/logo";

const FONT = "Arial, Helvetica, sans-serif";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Een telefoonnummer mag in de tekst staan zoals iemand het schrijft
 * ("06 - 12 34 56 78"), maar in de tel:-link hoort alleen wat een toestel kan
 * kiezen. Alles behalve cijfers en een leidende plus gaat eruit.
 */
function telHref(value: string): string {
  const digits = value.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return `tel:${escapeHtml(digits)}`;
}

/** Zonder schema wordt een website in mail als relatieve link behandeld en sneuvelt hij. */
function webHref(value: string): string {
  const trimmed = value.trim();
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return escapeHtml(withScheme);
}

/** In de zichtbare tekst hoeft "https://" niet; dat is ruis in een handtekening. */
function webLabel(value: string): string {
  return escapeHtml(value.trim().replace(/^https?:\/\//i, "").replace(/\/$/, ""));
}

function mailHref(value: string): string {
  return `mailto:${escapeHtml(value.trim())}`;
}

/** Een adres van meerdere regels blijft in mail alleen overeind met <br>. */
function multiline(value: string): string {
  return escapeHtml(value.trim()).replace(/\r?\n/g, "<br />");
}

function link(href: string, label: string, color: string, underline = false): string {
  const decoration = underline ? "underline" : "none";
  return `<a href="${href}" style="color:${color};text-decoration:${decoration}">${label}</a>`;
}

/** Laat een regel weg zodra het veld leeg is; een lege "T:" is slordiger dan geen T. */
function optional(value: string, render: (value: string) => string): string {
  return value.trim() === "" ? "" : render(value.trim());
}

function joinLines(parts: string[]): string {
  return parts.filter((p) => p !== "").join("<br />\n  ");
}

/** De naam-en-functieregel die vrijwel elk sjabloon nodig heeft. */
function rol(f: SignatureFields): string {
  const delen = [f.functie.trim(), f.bedrijf.trim()].filter((d) => d !== "");
  return delen.join(" — ");
}

function logoImg(logo: string | null, hoogte: number, extra = ""): string {
  if (logo === null) return "";
  // width bewust NIET zetten: een logo is zelden vierkant, en Outlook rekt het
  // uit zodra je beide opgeeft. Alleen de hoogte vastzetten houdt de verhouding.
  return `<img src="${logo}" alt="" height="${hoogte}" style="height:${hoogte}px;width:auto;border:0${extra}" />`;
}

export interface SignatureTemplate {
  id: string;
  naam: string;
  omschrijving: string;
  build: (fields: SignatureFields, logo: string | null) => string;
}

export const TEMPLATES: SignatureTemplate[] = [
  {
    id: "klassiek",
    naam: "Klassiek",
    omschrijving: "Naam, functie en contactgegevens onder elkaar. Werkt overal.",
    build: (f, logo) => {
      const a = f.accent;
      const contact = joinLines([
        optional(f.telefoon, (v) => `T: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.mobiel, (v) => `M: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.email, (v) => `E: ${link(mailHref(v), escapeHtml(v), a)}`),
        optional(f.website, (v) => `W: ${link(webHref(v), webLabel(v), a)}`),
      ]);
      return [
        `<div style="font-family:${FONT};font-size:13px;line-height:1.5;color:#333333">`,
        logo === null ? "" : `  <div style="margin:0 0 10px">${logoImg(logo, 44)}</div>`,
        `  <p style="margin:0 0 8px">`,
        `    <strong style="font-size:15px;color:#111111">${escapeHtml(f.naam)}</strong>`,
        optional(rol(f), (v) => `<br />\n    <span style="color:#666666">${escapeHtml(v)}</span>`),
        `  </p>`,
        contact === "" ? "" : `  <p style="margin:0">\n  ${contact}\n  </p>`,
        optional(f.adres, (v) => `  <p style="margin:8px 0 0;color:#888888;font-size:12px">${multiline(v)}</p>`),
        `</div>`,
      ].filter((r) => r !== "").join("\n");
    },
  },

  {
    id: "accentlijn",
    naam: "Accentlijn",
    omschrijving: "Streep in uw eigen kleur langs de linkerkant. Rustig en modern.",
    build: (f, logo) => {
      const a = f.accent;
      const contact = joinLines([
        optional(f.telefoon, (v) => link(telHref(v), escapeHtml(v), a)),
        optional(f.mobiel, (v) => link(telHref(v), escapeHtml(v), a)),
        optional(f.email, (v) => link(mailHref(v), escapeHtml(v), a)),
        optional(f.website, (v) => link(webHref(v), webLabel(v), a)),
      ]);
      return [
        `<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT};font-size:13px;line-height:1.6;color:#333333">`,
        `  <tr>`,
        `    <td style="border-left:3px solid ${a};padding-left:14px">`,
        logo === null ? "" : `      <div style="margin:0 0 8px">${logoImg(logo, 38)}</div>`,
        `      <div style="font-size:15px;font-weight:bold;color:#111111">${escapeHtml(f.naam)}</div>`,
        optional(rol(f), (v) => `      <div style="color:#777777;margin:0 0 6px">${escapeHtml(v)}</div>`),
        contact === "" ? "" : `      <div>\n  ${contact}\n      </div>`,
        optional(f.adres, (v) => `      <div style="color:#999999;font-size:12px;margin:6px 0 0">${multiline(v)}</div>`),
        `    </td>`,
        `  </tr>`,
        `</table>`,
      ].filter((r) => r !== "").join("\n");
    },
  },

  {
    id: "logo-naast",
    naam: "Logo ernaast",
    omschrijving: "Uw logo links, de gegevens rechts. De klassieke zakelijke vorm.",
    build: (f, logo) => {
      const a = f.accent;
      const contact = joinLines([
        optional(f.telefoon, (v) => `T: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.mobiel, (v) => `M: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.email, (v) => `E: ${link(mailHref(v), escapeHtml(v), a)}`),
        optional(f.website, (v) => `W: ${link(webHref(v), webLabel(v), a)}`),
      ]);
      const rechts = [
        `        <div style="font-size:15px;font-weight:bold;color:#111111">${escapeHtml(f.naam)}</div>`,
        optional(rol(f), (v) => `        <div style="color:#777777;margin:0 0 6px">${escapeHtml(v)}</div>`),
        contact === "" ? "" : `        <div>\n  ${contact}\n        </div>`,
        optional(f.adres, (v) => `        <div style="color:#999999;font-size:12px;margin:6px 0 0">${multiline(v)}</div>`),
      ].filter((r) => r !== "").join("\n");

      // Geen logo? Dan vervalt de linkerkolom; een lege cel met padding laat
      // anders een onverklaarbare inspringing achter.
      if (logo === null) {
        return [
          `<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT};font-size:13px;line-height:1.6;color:#333333">`,
          `  <tr>`,
          `    <td valign="top">`,
          rechts,
          `    </td>`,
          `  </tr>`,
          `</table>`,
        ].join("\n");
      }

      return [
        `<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT};font-size:13px;line-height:1.6;color:#333333">`,
        `  <tr>`,
        `    <td valign="top" style="padding-right:16px;border-right:1px solid #e0e0e0">`,
        `      ${logoImg(logo, 54)}`,
        `    </td>`,
        `    <td valign="top" style="padding-left:16px">`,
        rechts,
        `    </td>`,
        `  </tr>`,
        `</table>`,
      ].join("\n");
    },
  },

  {
    id: "compact",
    naam: "Compact",
    omschrijving: "Twee regels, alles achter elkaar. Voor wie veel mailt.",
    build: (f, logo) => {
      const a = f.accent;
      const scheiding = `<span style="color:#cccccc"> · </span>`;
      const contact = [
        optional(f.telefoon, (v) => link(telHref(v), escapeHtml(v), a)),
        optional(f.mobiel, (v) => link(telHref(v), escapeHtml(v), a)),
        optional(f.email, (v) => link(mailHref(v), escapeHtml(v), a)),
        optional(f.website, (v) => link(webHref(v), webLabel(v), a)),
      ].filter((p) => p !== "").join(scheiding);

      const kop = [
        `<strong style="color:#111111">${escapeHtml(f.naam)}</strong>`,
        optional(rol(f), (v) => `<span style="color:#777777">${escapeHtml(v)}</span>`),
      ].filter((p) => p !== "").join(scheiding);

      return [
        `<div style="font-family:${FONT};font-size:13px;line-height:1.6;color:#333333">`,
        logo === null ? "" : `  <div style="margin:0 0 6px">${logoImg(logo, 32)}</div>`,
        `  <div>${kop}</div>`,
        contact === "" ? "" : `  <div>${contact}</div>`,
        `</div>`,
      ].filter((r) => r !== "").join("\n");
    },
  },

  {
    id: "zakelijk",
    naam: "Zakelijk",
    omschrijving: "Bedrijfsnaam in uw kleur, met een scheidingslijn boven de gegevens.",
    build: (f, logo) => {
      const a = f.accent;
      const contact = [
        optional(f.telefoon, (v) => `T ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.mobiel, (v) => `M ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.email, (v) => `E ${link(mailHref(v), escapeHtml(v), a)}`),
        optional(f.website, (v) => `W ${link(webHref(v), webLabel(v), a)}`),
      ].filter((p) => p !== "").join(`<span style="color:#cccccc">&nbsp;&nbsp;|&nbsp;&nbsp;</span>`);

      return [
        `<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT};font-size:13px;line-height:1.6;color:#444444">`,
        `  <tr>`,
        `    <td style="padding:0 0 10px">`,
        logo === null ? "" : `      <div style="margin:0 0 8px">${logoImg(logo, 40)}</div>`,
        `      <div style="font-size:16px;font-weight:bold;color:#111111;letter-spacing:0.01em">${escapeHtml(f.naam)}</div>`,
        optional(f.functie, (v) => `      <div style="color:#777777">${escapeHtml(v)}</div>`),
        optional(f.bedrijf, (v) => `      <div style="color:${a};font-weight:bold;text-transform:uppercase;font-size:12px;letter-spacing:0.08em;margin:4px 0 0">${escapeHtml(v)}</div>`),
        `    </td>`,
        `  </tr>`,
        `  <tr>`,
        `    <td style="border-top:2px solid ${a};padding:10px 0 0;font-size:12px">`,
        contact === "" ? "" : `      <div>${contact}</div>`,
        optional(f.adres, (v) => `      <div style="color:#999999;margin:6px 0 0">${multiline(v)}</div>`),
        `    </td>`,
        `  </tr>`,
        `</table>`,
      ].filter((r) => r !== "").join("\n");
    },
  },

  {
    id: "formeel",
    naam: "Formeel",
    omschrijving: "Met adresblok eronder. Voor post die de deur uit gaat namens het bedrijf.",
    build: (f, logo) => {
      const a = f.accent;
      const contact = joinLines([
        optional(f.telefoon, (v) => `Telefoon: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.mobiel, (v) => `Mobiel: ${link(telHref(v), escapeHtml(v), a)}`),
        optional(f.email, (v) => `E-mail: ${link(mailHref(v), escapeHtml(v), a)}`),
        optional(f.website, (v) => `Website: ${link(webHref(v), webLabel(v), a)}`),
      ]);
      return [
        `<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT};font-size:13px;line-height:1.55;color:#333333">`,
        `  <tr>`,
        `    <td style="padding:0 0 12px">`,
        `      <div style="font-size:15px;font-weight:bold;color:#111111">${escapeHtml(f.naam)}</div>`,
        optional(f.functie, (v) => `      <div style="color:#777777;font-style:italic">${escapeHtml(v)}</div>`),
        `    </td>`,
        `  </tr>`,
        `  <tr>`,
        `    <td style="border-top:1px solid #dddddd;padding:12px 0 0">`,
        logo === null
          ? optional(f.bedrijf, (v) => `      <div style="font-weight:bold;color:${a};margin:0 0 6px">${escapeHtml(v)}</div>`)
          : `      <div style="margin:0 0 8px">${logoImg(logo, 40)}</div>`,
        logo !== null
          ? optional(f.bedrijf, (v) => `      <div style="font-weight:bold;color:${a};margin:0 0 6px">${escapeHtml(v)}</div>`)
          : "",
        optional(f.adres, (v) => `      <div style="color:#666666;margin:0 0 6px">${multiline(v)}</div>`),
        contact === "" ? "" : `      <div style="font-size:12px">\n  ${contact}\n      </div>`,
        `    </td>`,
        `  </tr>`,
        `</table>`,
      ].filter((r) => r !== "").join("\n");
    },
  },
];

/**
 * Invulwaarden voor de miniatuur in de sjabloonkiezer. Zonder dit staat er bij
 * een lege handtekening zes keer een leeg kaartje, en dan is er niets te kiezen.
 * Ze worden NOOIT opgeslagen — alleen getekend.
 */
const DEMO: SignatureFields = {
  naam: "Uw Naam",
  functie: "Functie",
  bedrijf: "Bedrijfsnaam",
  telefoon: "010 - 123 45 67",
  mobiel: "",
  email: "naam@uwdomein.nl",
  website: "uwdomein.nl",
  adres: "Straatnaam 1, 3011 AA Rotterdam",
  accent: "#fe7b05",
};

/** Vult alleen de lege velden aan, zodat de miniatuur uw eigen gegevens toont zodra u ze invult. */
export function withDemoFallback(fields: SignatureFields): SignatureFields {
  const leeg = (["naam", "functie", "bedrijf", "telefoon", "email", "website", "adres"] as const)
    .every((k) => fields[k].trim() === "");
  if (!leeg) return fields;
  return { ...DEMO, accent: fields.accent };
}
